import React, { ReactElement } from "react";
import SkeletonHeader from "./SkeletonHeader";
import IconSkeleton from "./IconSkeleton";

type ChartType = "funnel" | "bar" | "pie";

const FUNNEL_WIDTHS = [100, 82, 64, 46, 28];
const BAR_HEIGHTS = [20, 40, 55, 30, 50];

const ChartSkeleton = ({
  className = "",
  chartTypes = ["funnel", "bar"],
}: {
  className?: string;
  chartTypes?: ChartType[];
}) => {
  const renderFunnelSkeleton = (): ReactElement => (
    <div className="col-12 col-lg-6">
      <div className="apple-card gestalt-group group border-0 shadow-sm rounded-5 p-4 h-100 skeleton-card">
        <div className="d-flex align-items-center gap-3 mb-4 flex-wrap">
          <IconSkeleton size={44} />
          <div className="w-100">
            <SkeletonHeader titleWidth="40%" subtitleWidth="55%" />
          </div>
        </div>

        <div className="d-flex align-items-center flex-column gap-3 mb-4">
          {FUNNEL_WIDTHS.map((width, index) => (
            <div
              key={index}
              className="skeleton-wave-block skeleton-medium skeleton-pill"
              style={{
                width: `${width}%`,
                minHeight: 28,
                maxWidth: "100%",
              }}
            />
          ))}
        </div>

        <div className="d-flex flex-column gap-2">
          {Array.from({ length: 4 }).map((_, itemIndex) => (
            <div key={itemIndex} className="d-flex align-items-center gap-2">
              <div className="skeleton-wave-block skeleton-light skeleton-dot" />
              <div
                className="skeleton-wave-block skeleton-light skeleton-rect"
                style={{
                  width:
                    itemIndex === 0
                      ? "40%"
                      : itemIndex === 1
                      ? "55%"
                      : itemIndex === 2
                      ? "35%"
                      : "50%",
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderBarSkeleton = (): ReactElement => (
    <div className="col-12 col-lg-6">
      <div className="apple-card gestalt-group group border-0 shadow-sm rounded-5 p-4 h-100 skeleton-card">
        <div className="d-flex align-items-center gap-3 mb-4 flex-wrap">
          <IconSkeleton size={44} />
          <div className="w-100">
            <SkeletonHeader titleWidth="50%" subtitleWidth="60%" />
          </div>
        </div>

        <div
            className="skeleton-wave-block skeleton-medium mb-4"
            style={{
              width: "100%",
              height: 260,
              overflow: "hidden",
              position: "relative",
            }}
        >
          <div
            className="position-absolute bottom-0 start-0 end-0 d-flex align-items-end justify-content-between px-3 pb-4"
            style={{ height: "100%" }}
          >
            {BAR_HEIGHTS.map((height, index) => (
              <div
                key={index}
                className="skeleton-wave-block skeleton-primary"
                style={{
                  width: 18,
                  height: `${height}%`,
                  borderRadius: 8,
                  flexShrink: 0,
                }}
              />
            ))}
          </div>
          <div className="position-absolute start-0 bottom-0 skeleton-secondary" style={{ width: "100%", height: 1 }} />
        </div>

        <div className="d-flex flex-wrap gap-3">
            {Array.from({ length: 3 }).map((_, itemIndex) => (
            <div
              key={itemIndex}
              className="skeleton-wave-block skeleton-light skeleton-rect"
              style={{
                width: itemIndex === 0 ? "30%" : itemIndex === 1 ? "25%" : "18%",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );

  const renderPieSkeleton = (): ReactElement => (
    <div className="col-12 col-lg-6">
      <div className="apple-card gestalt-group group border-0 shadow-sm rounded-5 p-4 h-100 skeleton-card">
        <div className="d-flex align-items-center gap-3 mb-4 flex-wrap">
          <IconSkeleton size={44} />
          <div className="w-100">
            <SkeletonHeader titleWidth="45%" subtitleWidth="58%" />
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-center py-3">
          <div
            className="skeleton-wave-block skeleton-dark rounded-circle"
            style={{
              width: 220,
              height: 220,
              position: "relative",
            }}
          >
            <div
              className="position-absolute top-0 start-0 rounded-circle"
              style={{
                width: 220,
                height: 220,
                background: "conic-gradient(rgba(226, 232, 240, 0.2) 0deg 120deg, rgba(226, 232, 240, 0.9) 120deg 240deg, rgba(226, 232, 240, 0.45) 240deg 360deg)",
                mask: "radial-gradient(farthest-side, transparent calc(100% - 24px), #000 0)",
              }}
            />
          </div>
        </div>

        <div className="d-flex flex-wrap gap-3 justify-content-center">
          {Array.from({ length: 3 }).map((_, itemIndex) => (
            <div key={itemIndex} className="d-flex align-items-center gap-2">
                <div className="skeleton-wave-block skeleton-light skeleton-dot" />
                <div
                  className={`skeleton-wave-block skeleton-light ${itemIndex === 0 ? "skeleton-legend-text-lg" : itemIndex === 1 ? "skeleton-legend-text-md" : "skeleton-legend-text-sm"}`}
                />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className={`w-100 ${className}`}>
      <div className="row g-4">
        {chartTypes.map((type, idx) => {
          const chartMap: Record<ChartType, () => ReactElement> = {
            funnel: renderFunnelSkeleton,
            bar: renderBarSkeleton,
            pie: renderPieSkeleton,
          };
          const renderFn = chartMap[type];
          return renderFn ? React.cloneElement(renderFn(), { key: `${type}-${idx}` }) : null;
        })}
      </div>
    </div>
  );
};

export default ChartSkeleton;
